import { useAppState } from '@/composables/useAppState.ts'
import { computed, ref } from 'vue'
import { usePwa } from '@/composables/usePwa.ts'

interface VersionJson {
  version: string
  build: string
}

export const VERSION_URL = import.meta.env.BASE_URL + '/version.json'
export const API_HASH_URL = new URL('/api-hash', useAppState().backendUrl.value).toString()
export const API_HASH_EXPECTED = __API_HASH__
export const VERSION_EXPECTED: VersionJson = {
  version: __BUILD_VERSION__,
  build: __BUILD_DATE__
}

const VERSION_REMOTE = ref<VersionJson>()
const API_HASH_REMOTE = ref<string>()

const API_HASH_MISSMATCH = computed(() => !!API_HASH_EXPECTED && !!API_HASH_REMOTE.value && API_HASH_REMOTE.value != API_HASH_EXPECTED)
const VERSION_MISSMATCH = computed(() => !!VERSION_REMOTE.value && VERSION_REMOTE.value.version != VERSION_EXPECTED.version)
const BUILD_MISSMATCH = computed(() => !!VERSION_REMOTE.value && VERSION_REMOTE.value.build != VERSION_EXPECTED.build)

const ANY_VERSION_MISSMATCH = computed(() => VERSION_MISSMATCH.value || BUILD_MISSMATCH.value)

const CHECK_INTERVAL = ref<number>()

const pwa = usePwa()
watch(pwa.isReloadPending, (x) => {
  console.log(`pwa.isUpdateAvailable -> ${x}`)
})
setTimeout(() => {
  void useVersionCheck().checkVersion()
  void useVersionCheck().checkApi()
}, 3000)

export function useVersionCheck() {
  async function checkApi(): Promise<void> {
    if (!API_HASH_EXPECTED) return //skip if no api hash was compiled in
    try {
      const response = await fetch(API_HASH_URL, {
        method: 'GET',
        cache: 'no-store',
        keepalive: false
      })
      const json = (await response.json()) as { apiHash: string } | undefined
      API_HASH_REMOTE.value = json?.apiHash
    } catch {
      API_HASH_REMOTE.value = undefined
    }
  }

  async function checkVersion(): Promise<void> {
    if (import.meta.env.DEV) return //skip if in dev
    try {
      const response = await fetch(VERSION_URL + '?ts=' + Date.now(), {
        method: 'GET',
        cache: 'no-store',
        keepalive: false
      })
      VERSION_REMOTE.value = (await response.json()) as VersionJson | undefined
    } catch {
      VERSION_REMOTE.value = undefined
    }
  }

  function start(interval?: number): void {
    if (import.meta.env.DEV) return //skip if in dev
    if (typeof CHECK_INTERVAL.value != 'undefined') {
      stop()
    }
    CHECK_INTERVAL.value = window.setInterval(() => {
      void checkVersion()
      void checkApi()
    }, interval ?? 30000)
  }

  function stop(): void {
    if (typeof CHECK_INTERVAL.value == 'undefined') return
  }

  function doUpdate() {
    setTimeout(() => window.location.reload(), 5000)
  }

  return {
    checkVersion,
    checkApi,

    doUpdate,

    start,
    stop,

    apiHashExpected: API_HASH_EXPECTED,
    versionExpected: __BUILD_VERSION__,
    buildExpected: __BUILD_DATE__,
    apiHashRemote: readonly(API_HASH_REMOTE),
    versionRemote: readonly(computed(() => VERSION_REMOTE.value?.version)),
    buildRemote: readonly(computed(() => VERSION_REMOTE.value?.build)),

    apiHashMissmatch: readonly(API_HASH_MISSMATCH),
    versionMissmatch: readonly(VERSION_MISSMATCH),
    buildMissmatch: readonly(BUILD_MISSMATCH),

    anyVersionMissmatch: ANY_VERSION_MISSMATCH
  }
}
